/*
  The design tokens a ranch theme may override, and the CSS custom property each maps to.

  THIS FILE MIRRORS TWO OTHERS

  The names are the ones in the brand stylesheets (plus the three app.css adds) with the
  `--color-` prefix removed, and they are the names `Core.Branding.ThemeTokens` (API solution)
  accepts. The API validates a name it can refuse before a write, the stylesheet declares
  a default the browser falls back to, and this file knows which prefix to put back on.
  A sync test on the C# side asserts the two sets match (conventions.md §7).

  Everything else is fixed for every ranch (brief §2.8): typefaces, spacing, radius, page
  and surface backgrounds, primary text, status colours and the map palette carry
  meaning or legibility and are deliberately absent. The API refuses them by name.
*/

export const COLOR_TOKENS = [
  'accent',
  'accent-hover',
  'accent-text',
  'secondary',
  'secondary-hover',
  'nav-bg',
  'nav-bg-hover',
  'nav-bg-active',
] as const

export type ColorToken = (typeof COLOR_TOKENS)[number]

export const TOKEN_PREFIX = {
  colors: '--color-',
} as const

/** Readable labels for the brand editor. The hint is the rule, stated where it is followed. */
export const COLOR_LABELS: Record<ColorToken, { label: string; hint: string }> = {
  accent: {
    label: 'Accent',
    hint: 'Primary buttons, calls to action and accent moments. Replaces Brand Rust.',
  },
  'accent-hover': { label: 'Accent (hover)', hint: 'Derived: a step darker than the accent.' },
  'accent-text': {
    label: 'Accent (text)',
    hint: 'Derived: the accent darkened until links and small text reach 4.5:1 on the page.',
  },
  secondary: {
    label: 'Secondary',
    hint: 'Secondary and outline buttons, eyebrow labels. Replaces Saddle Brown.',
  },
  'secondary-hover': {
    label: 'Secondary (hover)',
    hint: 'Derived: a step darker than the secondary.',
  },
  'nav-bg': { label: 'Sidebar', hint: 'The dark navigation background.' },
  'nav-bg-hover': { label: 'Sidebar (hover)', hint: 'Derived: a step lighter than the sidebar.' },
  'nav-bg-active': {
    label: 'Sidebar (active)',
    hint: 'Derived: two steps lighter than the sidebar.',
  },
}

/** Every custom property a theme can write, as one flat list. */
export function allThemeProperties(): string[] {
  return COLOR_TOKENS.map((t) => `${TOKEN_PREFIX.colors}${t}`)
}

export function isColorToken(name: string): name is ColorToken {
  return (COLOR_TOKENS as readonly string[]).includes(name)
}
